import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

const STORAGE_KEY = "mdsync-theme";

type Theme = "light" | "dark" | "system";
type EffectiveTheme = "light" | "dark";

interface ThemeState {
  theme: Theme;
  effectiveTheme: EffectiveTheme;
  setTheme: (theme: Theme) => void;
}

function getEffectiveTheme(theme: Theme): EffectiveTheme {
  if (theme === "dark") return "dark";
  if (theme === "light") return "light";
  return typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const effectiveTheme = getEffectiveTheme(theme);
  root.classList.toggle("dark", effectiveTheme === "dark");
  useThemeStore.setState({ effectiveTheme });
}

/** Read theme from localStorage synchronously (same key/structure as persist) */
function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return "system";
    const data = JSON.parse(raw) as {
      state?: { theme?: string };
      theme?: string;
    };
    const theme = data?.state?.theme ?? data?.theme;
    if (theme === "dark" || theme === "light" || theme === "system") {
      return theme;
    }
  } catch {
    /* ignore */
  }
  return "system";
}

const initialTheme = getStoredTheme();

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: initialTheme,
      effectiveTheme: getEffectiveTheme(initialTheme),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (state) => {
        applyTheme(state?.theme ?? "system");
      },
    },
  ),
);

// Apply stored theme immediately so correct theme is active before any component mounts
if (typeof window !== "undefined") {
  applyTheme(initialTheme);
}

// Listen for system theme changes when in "system" mode
if (typeof window !== "undefined") {
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => {
      const { theme } = useThemeStore.getState();
      if (theme === "system") applyTheme("system");
    });
}
